import React, { useState, useEffect } from 'react';
import { API } from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const WorkerCustomForms = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = API.replace(/\/api$/, '');

  useEffect(() => {
    fetchRequiredForms();
  }, []);

  const fetchRequiredForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/worker/required-forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForms(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching forms:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">{t('forms.loadingForms')}</div>
      </div>
    );
  }

  const completedForms = forms.filter(f => f.completed);
  const pendingForms = forms.filter(f => !f.completed);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('forms.myForms')}</h1>
        <p className="text-gray-600 mb-6">{t('forms.completeAllForms')}</p>

        {forms.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">{t('forms.formCompletion')}</h3>
                <p className="text-sm text-gray-600">
                  {t('forms.formsCompleted', { completed: completedForms.length, total: forms.length })}
                </p>
              </div>
              <div className="text-3xl font-bold text-orange-600">
                {Math.round((completedForms.length / forms.length) * 100)}%
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-orange-600 h-3 rounded-full transition-all"
                style={{ width: `${(completedForms.length / forms.length) * 100}%` }}
              />
            </div>
          </div>
        )}

        {pendingForms.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('forms.requiredForms')} ({pendingForms.length})
            </h2>
            <div className="space-y-4">
              {pendingForms.map((form) => (
                <div key={form.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{form.formName}</h3>
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                          {t('forms.required')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {form.state ? `${form.state} ${t('forms.stateForm')}` : t('forms.federalForm')} &bull; {form.category}
                      </p>
                      <p className="text-sm text-gray-700">
                        {t('forms.fieldsToComplete', { count: form.fields?.length || 0 })}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/worker/forms/${form.id}/fill`)}
                      className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium"
                      data-testid={`fill-form-${form.id}`}
                    >
                      {t('forms.fillOutForm')} &rarr;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {completedForms.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('forms.completedForms')} ({completedForms.length})
            </h2>
            <div className="space-y-4">
              {completedForms.map((form) => (
                <div key={form.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{form.formName}</h3>
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {t('common.completed')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {form.state ? `${form.state} ${t('forms.stateForm')}` : t('forms.federalForm')} &bull; {form.category}
                      </p>
                      <p className="text-xs text-gray-500">{t('forms.needToUpdate')}</p>
                    </div>
                    <button
                      onClick={() => navigate(`/worker/forms/${form.id}/fill`)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                    >
                      {t('forms.updateForm')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {forms.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('forms.noFormsAssigned')}</h3>
            <p className="text-gray-500">{t('forms.noFormsDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerCustomForms;
